<template>
  <v-container fluid pt-0>
    <v-row align="center" class="pb-2">
      <h1 class="headline">Allocations</h1>
      <v-spacer></v-spacer>
    </v-row>
    <v-row>
      <v-col class="py-0 px-0" cols="4">
        <dates-range-picker
          :gt-date-from-string="startDate"
          :gt-date-to-string="endDate"
          :outlined="outlinedInputs"
          @onRangeSelected="onDatesChange"
          dense
        ></dates-range-picker>
      </v-col>
      <v-col class="py-0">
        <destination-list-select
          v-model="destinationId"
          :outlined="outlinedInputs"
          :set-default="true"
          hide-details
          dense
          @input="onDestinationChange"
        ></destination-list-select>
      </v-col>
      <v-col class="py-0">
        <supplier-type-multi-filter
          v-model="supplierTypes"
          :outlined="outlinedInputs"
          dense
          hide-details
          @input="onSupplierTypesChange"
        />
      </v-col>
      <v-col class="py-0">
        <v-text-field
          label="Supplier Name"
          v-model="supplierNameFilter"
          clearable
          :outlined="outlinedInputs"
          hide-details
          dense
          @change="onSupplierNameChange"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col class="py-0">
        <v-radio-group v-model="displayValue" row label="Show: ">
          <v-radio label="Allocation" value="qty"></v-radio>
          <v-radio label="Available" value="remain"></v-radio>
          <v-radio label="Used" value="taken"></v-radio>
        </v-radio-group>
      </v-col>
      <v-spacer></v-spacer>
      <ag-grid-switch name="allocPlugin" class="mr-2"></ag-grid-switch>
    </v-row>
    <v-row v-if="showAgGrid">
      <v-col>
        <ag-grid-vue
          class="ag-theme-alpine"
          id="alloc-plugin-ag-grid"
          :rowData="items"
          :columnDefs="agGridColumns"
          rowSelection="multiple"
          :context="agContext"
          @grid-ready="onAgGridReady"
          @columnMoved="onAgColumnMoved"
          @columnResized="onAgColumnResized"
          @sortChanged="onAgSortChanged"
          @selection-changed="onAgSelectionChanged"
          @newColumnsLoaded="onAgNewColumns"
          suppressDragLeaveHidesColumns
        >
        </ag-grid-vue>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col class="pa-0">
        <v-data-table
          class="elevation-1"
          :headers="headers"
          :items="items"
          :loading="$apollo.loading"
        >
          <template v-for="(k, i) in daysKeys" v-slot:[`item.${k}`]="{ item }">
            <v-chip
              :key="i"
              :color="item[k] > 0 ? `green lighten-4` : `red lighten-3`"
              v-if="displayValue === 'taken' && item[k] > 0"
              @click="onAllocationClick(item, k)"
              >{{ item[k] }}</v-chip
            >
            <v-chip
              :key="i"
              :color="item[k] > 0 ? `green lighten-4` : `red lighten-3`"
              v-else
              >{{ item[k] }}</v-chip
            >
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-row v-if="debug">
      <code>{{ filters }}</code>
    </v-row>
    <allocation-usage-details-dialog
      :show="showUsageDetails"
      @close="showUsageDetails = false"
      v-model="selectedAllocationItem"
    ></allocation-usage-details-dialog>
  </v-container>
</template>

<script>
import gql from "graphql-tag";
import DatesRangePicker from "../../shared/DatesRangePicker.vue";
import DestinationListSelect from "../../shared/DestinationListSelect.vue";
import SupplierTypeMultiFilter from "../../shared/list-filters/SupplierTypeMultiFilter.vue";
import { groupByFn, removeGQLTypename } from "../../../../helpers";
import AgGridListMixin from "../../../../mixins/AgGridListMixin";
import AgGridSwitch from "../../shared/AgGridSwitch.vue";
import { AgGridVue } from "ag-grid-vue";
import AllocationNumberLabel from "./AllocationNumberLabel.vue";
import AllocationUsageDetailsDialog from "../../suppliers/allocations/AllocationUsageDetailsDialog.vue";

const moment = require("moment");
const ALLOCATIONS = gql`
  query allAllcations($filter: AllocationFilterType) {
    allocations(filter: $filter) {
      edges {
        node {
          service {
            id
            name
            stdOccupancy
            supplier {
              id
              name
              serviceType
            }
          }
          id
          uid
          name
          date
          qty
          taken
          type
          maxTotal
        }
      }
    }
  }
`;
export default {
  name: "allocations-plugin",

  components: {
    DatesRangePicker,
    DestinationListSelect,
    SupplierTypeMultiFilter,
    AgGridSwitch,
    AgGridVue,
    AllocationNumberLabel,
    AllocationUsageDetailsDialog,
  },

  mixins: [AgGridListMixin],

  data() {
    const filters = this.$store.getters.localSettings("allocPluginfilters", {
      startDate: moment(new Date()).toISOString(true),
      endDate: moment(new Date()).add(7, "days").toISOString(true),
      destinationId: "",
      supplierTypes: [],
      supplierNameFilter: "",
      displayValue: "qty",
    });
    return {
      debug: false,
      startDate: filters.startDate,
      endDate: filters.endDate,
      destinationId: filters.destinationId,
      supplierTypes: filters.supplierTypes,
      supplierNameFilter: filters.supplierNameFilter,
      displayValue: filters.displayValue,
      showUsageDetails: false,
      selectedAllocationItem: null,
      staticHeaders: [
        {
          text: "Supplier",
          value: "supplierName",
          name: "supplierName",
        },
        {
          text: "Service",
          value: "serviceName",
          name: "serviceName",
        },
        {
          text: "Alloc Name",
          value: "name",
          name: "name",
        },
      ],
      agContext: {
        onAllocationClick: null,
        displayValue: null,
      },
    };
  },

  computed: {
    filters() {
      return this.$store.getters.localSettings("allocPluginfilters", {
        startDate: moment(new Date()).toISOString(true),
        endDate: moment(new Date()).add(7, "days").toISOString(true),
        destinationId: "",
        supplierTypes: [],
        supplierNameFilter: "",
        displayValue: "qty",
      });
    },
    showAgGrid() {
      return this.$store.getters.localSettings("allocPlugin-showAg", false);
    },
    daysKeys() {
      return this.days.map((d) => d.format("YYYY-MM-DD"));
    },
    days() {
      const days = [];
      const a = moment(this.startDate);
      const b = moment(this.endDate);
      for (const m = moment(a); m.diff(b, "days") <= 0; m.add(1, "days")) {
        days.push(moment(m));
      }
      return days;
    },
    byAllocation() {
      const allocations =
        this.allocations && this.allocations.edges
          ? this.allocations.edges.map(removeGQLTypename)
          : [];
      //console.log(allocations);
      return allocations.length > 0
        ? groupByFn(allocations, this.groupingFn)
        : {};
    },
    items() {
      const list = Object.keys(this.byAllocation).reduce((items, uid) => {
        const allocs = this.byAllocation[uid];
        const alloc = allocs[0];
        const item = {
          uid: alloc.uid,
          name: alloc.name,
          supplierName: alloc.service.supplier.name,
          serviceName: alloc.service.name,
          serviceId: alloc.service.id,
        };
        for (const day of this.days) {
          const dateKey = day.format("YYYY-MM-DD");
          const allocDay = allocs.find((a) => a.date == dateKey);
          item[dateKey] = "-";
          if (allocDay) {
            if (this.displayValue == "qty") {
              //console.log(allocDay);
              item[dateKey] = allocDay.qty;
            } else if (this.displayValue == "remain") {
              item[dateKey] = allocDay.qty - allocDay.taken;
            } else {
              item[dateKey] = allocDay.taken;
            }
          }
        }
        items.push(item);
        return items;
      }, []);
      return list;
    },
    filteredHeaders() {
      return this.headers;
    },
    headers() {
      return [
        ...this.staticHeaders,
        ...this.days.map((d) => ({
          text: d.format(this.$store.getters.userConfig.dateFormat),
          value: d.format("YYYY-MM-DD"),
          name: "allocData",
          cellRendererFramework: "AllocationNumberLabel",
          valueGetter: (payload) => payload.data[d.format("YYYY-MM-DD")],
        })),
      ];
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
  },

  watch: {
    displayValue() {
      this.agContext.displayValue = this.displayValue;
    },
  },

  methods: {
    groupingFn(item) {
      return `${item.uid}_${item.service.id}`;
    },
    onDatesChange(range) {
      this.startDate = range.dateFrom;
      this.endDate = range.dateTo;
      const filters = { ...this.filters };
      filters.startDate = this.startDate;
      filters.endDate = this.endDate;
      this.$store.dispatch("updateLocalSettings", {
        allocPluginfilters: filters,
      });
    },
    onDestinationChange() {
      this.$store.dispatch("updateLocalSettings", {
        allocPluginfilters: {
          ...this.filters,
          destinationId: this.destinationId,
        },
      });
    },
    onSupplierTypesChange() {
      this.$store.dispatch("updateLocalSettings", {
        allocPluginfilters: {
          ...this.filters,
          supplierTypes: this.supplierTypes,
        },
      });
    },
    onSupplierNameChange() {
      this.$store.dispatch("updateLocalSettings", {
        allocPluginfilters: {
          ...this.filters,
          supplierNameFilter: this.supplierNameFilter,
        },
      });
    },
    onAllocationClick(item, k) {
      let dateKey = k;
      if (k.startsWith("allocData_")) {
        const columnIndex = parseInt(k.split("_")[1]);
        if (columnIndex >= 0 && columnIndex < this.daysKeys.length) {
          dateKey = this.daysKeys[columnIndex];
        }
      }

      // If showing "Used" values and the value is greater than 0, show usage details
      if (this.displayValue === "taken" && item[dateKey] > 0) {
        // Find the allocation data for this specific date and item
        // Use the same grouping key as used in byAllocation
        const allocationKey = `${item.uid}_${item.serviceId}`;

        const allocs = this.byAllocation[allocationKey];

        if (allocs) {
          const allocDay = allocs.find((a) => a.date === dateKey);

          if (allocDay) {
            this.selectedAllocationItem = {
              uid: item.uid,
              date: dateKey,
              service: {
                id: allocDay.service.id,
              },
            };
            this.showUsageDetails = true;
          }
        }
      } else {
        console.log(item, k);
      }
    },
  },

  apollo: {
    allocations: {
      query: ALLOCATIONS,
      variables() {
        return {
          filter: {
            date_gte: this.startDate,
            date_lte: this.endDate,
            destinationId: this.destinationId,
            serviceTypes: [...this.supplierTypes],
            supplier_name_contains: this.supplierNameFilter,
          },
        };
      },
      fetchPolicy: "network-only",
    },
  },

  mounted() {
    this.agContext.onAllocationClick = this.onAllocationClick;
  },
};
</script>

<style>
#alloc-plugin-ag-grid {
  height: calc(100vh - 220px);
}
</style>



// WEBPACK FOOTER //
// src/components/gt/plugins/allocations/AllocationsPlugin.vue